import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, MapPin, Phone, Filter, Map, List } from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import { useData } from '../context/DataContext';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Clinicas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { detectedCountry, isLoading: countryLoading } = useCountry();
  const { clinics, specialties, countries, cities, loading: dataLoading } = useData();
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('pais') || detectedCountry || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('ciudad') || 'all');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('especialidad') || 'all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  // Auto-select detected country on mount or when it changes
  useEffect(() => {
    if (detectedCountry && !selectedCountry) {
      setSelectedCountry(detectedCountry);
    }
  }, [detectedCountry, selectedCountry]);

  // Filter clinics based on selections
  const filteredClinics = useMemo(() => {
    if (!clinics || clinics.length === 0) return [];
    
    return clinics.filter(clinic => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          clinic.name.toLowerCase().includes(searchLower) ||
          clinic.city.toLowerCase().includes(searchLower) ||
          clinic.description.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Country filter
      if (selectedCountry && clinic.country !== selectedCountry) {
        return false;
      }

      // City filter
      if (selectedCity !== 'all' && clinic.city !== selectedCity) {
        return false;
      }

      // Specialty filter
      if (selectedSpecialty !== 'all' && !clinic.specialties.includes(selectedSpecialty)) {
        return false;
      }

      return true;
    });
  }, [clinics, searchTerm, selectedCountry, selectedCity, selectedSpecialty]);

  // Get available cities for selected country
  const availableCities = selectedCountry && cities[selectedCountry] ? cities[selectedCountry] : [];

  // Reset city when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity('all');
    } else {
      // Check if current city is valid for new country
      const validCities = cities[selectedCountry] || [];
      if (selectedCity !== 'all' && !validCities.includes(selectedCity)) {
        setSelectedCity('all');
      }
    }
  }, [selectedCountry, selectedCity, cities]);

  // Calculate map center based on filtered clinics
  const mapCenter = useMemo(() => {
    if (!filteredClinics || filteredClinics.length === 0) {
      return [-16.5, -68.15]; // Default to Bolivia
    }
    const avgLat = filteredClinics.reduce((sum, c) => sum + c.coordinates.lat, 0) / filteredClinics.length;
    const avgLng = filteredClinics.reduce((sum, c) => sum + c.coordinates.lng, 0) / filteredClinics.length;
    return [avgLat, avgLng];
  }, [filteredClinics]);

  const getSpecialtyName = (id) => {
    if (!specialties) return id;
    const specialty = specialties.find(s => s.id === id);
    return specialty ? specialty.name : id;
  };

  const getCountryName = (code) => {
    if (!countries) return code;
    const country = countries.find(c => c.code === code);
    return country ? `${country.flag} ${country.name}` : code;
  };

  // Show loading state AFTER all hooks
  if (dataLoading || countryLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando clínicas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 py-8 relative z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Directorio de Clínicas</h1>
              <p className="text-gray-600">Encuentra la clínica dental perfecta para tus necesidades</p>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Buscar clínicas..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={selectedCity} 
              onValueChange={setSelectedCity}
              disabled={!selectedCountry}
            >
              <SelectTrigger>
                <SelectValue placeholder="Ciudad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las ciudades</SelectItem>
                {availableCities.map(city => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
              <SelectTrigger>
                <SelectValue placeholder="Especialidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las especialidades</SelectItem>
                {specialties.map(specialty => (
                  <SelectItem key={specialty.id} value={specialty.id}>
                    {specialty.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center justify-between mt-6">
            <p className="text-gray-600">
              {filteredClinics.length} clínica{filteredClinics.length !== 1 ? 's' : ''} encontrada{filteredClinics.length !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
              >
                <List className="h-4 w-4 mr-2" />
                Lista
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
              >
                <Map className="h-4 w-4 mr-2" />
                Mapa
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {viewMode === 'list' ? (
          // List View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClinics.map((clinic) => (
              <Card 
                key={clinic.id} 
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => navigate(`/clinicas/${clinic.id}`)}
              >
                <div className="relative overflow-hidden h-48">
                  <img
                    src={clinic.image}
                    alt={clinic.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                    {clinic.name}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="text-sm">{clinic.city}, {getCountryName(clinic.country)}</span>
                  </div>
                  {clinic.branches && clinic.branches.length > 1 && (
                    <div className="mb-2">
                      <Badge variant="outline" className="text-xs border-cyan-500 text-cyan-600">
                        {clinic.branches.length} sucursales
                      </Badge>
                    </div>
                  )}
                  <div className="flex items-center text-gray-600 mb-4">
                    <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="text-sm">{clinic.phone}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {clinic.specialties.slice(0, 3).map((spec, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-700 text-xs">
                        {getSpecialtyName(spec)}
                      </Badge>
                    ))}
                    {clinic.specialties.length > 3 && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700 text-xs">
                        +{clinic.specialties.length - 3} más
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          // Map View
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-0">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden relative z-0" style={{ height: '600px' }}>
                <MapContainer 
                  center={mapCenter} 
                  zoom={6} 
                  style={{ height: '100%', width: '100%', position: 'relative', zIndex: 0 }}
                  key={`${mapCenter[0]}-${mapCenter[1]}`}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {filteredClinics.map((clinic) => (
                    <Marker 
                      key={clinic.id} 
                      position={[clinic.coordinates.lat, clinic.coordinates.lng]}
                      eventHandlers={{
                        click: () => {
                          navigate(`/clinicas/${clinic.id}`);
                        }
                      }}
                    >
                      <Popup>
                        <div className="p-2">
                          <h3 className="font-bold text-gray-900 mb-1">{clinic.name}</h3>
                          <p className="text-sm text-gray-600 mb-1">{clinic.city}</p>
                          <p className="text-sm text-gray-600 mb-2">{clinic.phone}</p>
                          <button 
                            onClick={() => navigate(`/clinicas/${clinic.id}`)}
                            className="text-sm text-cyan-600 hover:text-cyan-700 font-semibold"
                          >
                            Ver detalles →
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-4 max-h-[600px] overflow-y-auto">
                <h3 className="font-bold text-lg mb-4">Clínicas ({filteredClinics.length})</h3>
                <div className="space-y-4">
                  {filteredClinics.map((clinic) => (
                    <div 
                      key={clinic.id} 
                      className="border-b border-gray-200 pb-4 last:border-b-0 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      onClick={() => navigate(`/clinicas/${clinic.id}`)}
                    >
                      <h4 className="font-semibold text-gray-900 mb-1 hover:text-cyan-600">{clinic.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <MapPin className="inline h-3 w-3 mr-1" />
                        {clinic.city}
                      </p>
                      <p className="text-sm text-gray-600">
                        <Phone className="inline h-3 w-3 mr-1" />
                        {clinic.phone}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredClinics.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No se encontraron clínicas con los criterios seleccionados.</p>
            <Button 
              className="mt-4 bg-cyan-500 hover:bg-cyan-600"
              onClick={() => {
                setSearchTerm('');
                setSelectedCountry(detectedCountry || '');
                setSelectedCity('all');
                setSelectedSpecialty('all');
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clinicas;
